import { FetchResult } from '@apollo/client';

import {
  clearAnalyticsDataLayer,
  updateAnalyticsDataLayer,
} from 'approot/shared/analytics/data-layer';
import {
  E_CLICKS_LOGGED_IN,
  pushEClicksDataEvent,
} from 'approot/shared/data-tracker/e-clicks-data-event';
import { pushDataTrackerEvent } from 'approot/shared/data-tracker/data-tracker-push';
import { DataTrackerEventData } from 'approot/shared/data-tracker/data-tracker.constants';
import {
  SKIP_CHOOSE_CLASS_REDIRECTION_PATH,
  GET_USER,
} from 'approot/shared/user/user-data.graphql';
import type { UserFragment } from '../user/user-data.graphql.generated';
import { isAccountInactive } from 'approot/shared/user/user-data';
import { setCurrentClass } from 'approot/classes/classes.apollo';
import { openInactiveAccountModal } from 'approot/global-notifications/global-notification.utils';
import {
  EVENT_ACTION_SIGN_IN,
  EVENT_ACTION_SIGN_OUT,
  EVENT_CATEGORY_AUTHENTICATION,
  EVENT_TARGET_TYPE_STUDENT,
  EVENT_TARGET_TYPE_TEACHER,
} from 'approot/shared/data-tracker/data-events.constants';
import {
  setUserData,
  clearUserData,
  updateAlreadySignedInOnce,
  getUserId,
  getToken,
} from 'approot/shared/api/auth/auth';
import {
  setStudentUserData,
  clearStudentUserData,
  getStudentUserData,
} from 'approot/shared/api/auth/student-user-auth';
import {
  clearStudentSession,
  getStudentData,
} from 'approot/student-class-to-do/student-class-to-do.utils';
import { StorageUserData } from 'approot/shared/api/auth/auth.constants';
import { PRIVATE_ACCOUNT_REACTIVATE } from 'global/private-routes.constants';
import { client as apolloClient } from '../../../apollo';
import { RememberType } from './signin.constants';
import type { StudentFragment } from '../../students/students.graphql.generated';
import {
  endIntercomSession,
  startAuthenticatedIntercomSession,
  startUnauthenticatedIntercomSession,
} from 'lib/intercom/intercom';
import { wsClient } from 'lib/ws/ws-client';
import type { SignOutMutation } from './signin.graphql.generated';
import { SIGN_OUT } from 'approot/shared/signin/signin.graphql';
import { GRAPHQL_REQUEST_TRACE_ID } from 'global/constants';
import { generateTraceId } from 'approot/shared/api/auth/generate-trace-id';
import { logException } from 'approot/shared/debug';
import { signInStateVar } from 'approot/shared/user/user-data.apollo';
import { resetClassSelectionVisible } from './signin-class-selection/signin-class-selection.apollo';
import {
  clearSessionData,
  initialiseSessionStorageDuration,
} from 'lib/session/session-storage-duration';
import {
  emitStudentSigninStorageEvent,
  emitStudentSignoutStorageEvent,
  emitTeacherSigninStorageEvent,
  emitTeacherSignoutStorageEvent,
} from 'lib/session/session-storage';
import { setStudentClassFromUrl } from './logic/set-student-class-from-url';
import { CLASS_SELECTION_PARAM } from './signin.constants';

export function handleSignInData({
  token,
  user,
  sessionAuthProvider,
}: {
  token: string;
  user: UserFragment;
  sessionAuthProvider?: string | null;
}) {
  const userId = user.id;

  initialiseSessionStorageDuration();
  emitTeacherSigninStorageEvent(token);

  const userWithSessionAuth = {
    ...user,
    sessionAuthProvider: sessionAuthProvider || null,
  };

  const data: StorageUserData = {
    token,
    user: userWithSessionAuth,
  };

  // add user data to storage
  setUserData(data, RememberType.LocalStorage);

  apolloClient.getClient().writeQuery({
    query: GET_USER,
    data: {
      user: userWithSessionAuth,
    },
  });

  updateSignInStateInCache({
    isSignedIn: true,
    isAlreadySignedInOnce: true,
    isStudentSignedIn: false,
  });

  updateAlreadySignedInOnce();
  updateAnalyticsDataLayer(user);

  if (userId) {
    if (
      isAccountInactive(user.accountStatus) &&
      user.hasStrongPassword &&
      window.location.pathname !== PRIVATE_ACCOUNT_REACTIVATE
    ) {
      openInactiveAccountModal();
    }
  }

  startAuthenticatedIntercomSession({
    id: user.id,
    email: user.email,
    hash: user.intercomHash,
    schoolId: user.school.id,
    schoolName: user.school.name,
    accountType: user.accountType,
  });
}

export function handleStudentUserSignin(
  token: string,
  studentUser: StudentFragment
) {
  const existingStudentUserData = getStudentUserData();

  // Check if the same student user is refreshing the session.
  // This ensures classId and classToDoId from session storage are preserved.
  const isSameStudentUser =
    existingStudentUserData?.studentUser.email === studentUser.email;

  setStudentUserData({
    ...(isSameStudentUser ? existingStudentUserData : {}),
    token,
    studentUser,
  });
  setStudentClassFromUrl(studentUser.currentClasses);
  emitStudentSigninStorageEvent(token);

  initialiseSessionStorageDuration();

  updateSignInStateInCache({
    isSignedIn: false,
    isAlreadySignedInOnce: true,
    isStudentSignedIn: true,
  });
}

function signOutAtServer(
  token: string | undefined
): Promise<void | FetchResult<SignOutMutation>> {
  const client = apolloClient.getClient();

  if (!token) return Promise.resolve();

  return client
    .mutate<SignOutMutation, null>({
      mutation: SIGN_OUT,
      fetchPolicy: 'network-only',
      context: {
        headers: {
          authorization: `Bearer ${token}`,
          [GRAPHQL_REQUEST_TRACE_ID]: generateTraceId(),
        },
      },
    })
    .catch(res => {
      logException({
        sourceFile: 'signin.utils.tsx',
        message: `There was an issue signing out at the server. Response status was: ${res.networkStatus}, ${res}`,
        statusCode: res.networkStatus,
      });
    });
}

export function signOut(props: {
  signOutServerSide: boolean;
  emitStorageEvent?: boolean;
}) {
  const { signOutServerSide = true, emitStorageEvent = true } = props || {};

  const userId = getUserId();
  const token = getToken();

  if (emitStorageEvent && userId && token) {
    emitTeacherSignoutStorageEvent(userId, token);
  }

  pushDataTrackerEvent(EVENT_CATEGORY_AUTHENTICATION, EVENT_ACTION_SIGN_OUT, {
    target_type: EVENT_TARGET_TYPE_TEACHER,
    target_identifier: userId,
  });

  clearUserData();
  clearSessionData();

  // sessionStorage can contain data queried by a logged in user
  // if location.state is utilised i.e. in the add teacher purchase flow

  clearAnalyticsDataLayer();

  // if there is an active websocket client close it
  if (wsClient) {
    wsClient.dispose();
  }

  setCurrentClass(undefined);

  // Reset class selection visibility state
  resetClassSelectionVisible();

  // this needs to be done first so resetStore re-queries
  // without authorization headers
  updateSignInStateInCache({
    isSignedIn: false,
    isAlreadySignedInOnce: true,
    isStudentSignedIn: false,
  });

  endIntercomSession();
  startUnauthenticatedIntercomSession();

  if (signOutServerSide && token) {
    apolloClient
      ?.getClient()
      ?.clearStore()
      .then(() => {
        signOutAtServer(token);
      });
  }
}

export function studentSignOut(
  props: {
    callback?: () => void;
    emitStorageEvent?: boolean;
    signOutServerSide?: boolean;
  } = {}
) {
  const {
    callback,
    emitStorageEvent = true,
    signOutServerSide = true,
  } = props || {};

  const studentUser = getStudentUserData();
  const studentData = getStudentData();
  const token = studentUser?.token || studentData?.token;

  // if there is a student user, we need to push a sign out event
  if (studentUser?.studentUser.id) {
    pushDataTrackerEvent(EVENT_CATEGORY_AUTHENTICATION, EVENT_ACTION_SIGN_OUT, {
      target_type: EVENT_TARGET_TYPE_STUDENT,
      target_identifier: studentUser.studentUser.id,
    });
  }

  if (emitStorageEvent && token) {
    emitStudentSignoutStorageEvent(token);
  }

  clearAnalyticsDataLayer();

  // if there is an active websocket client close it
  if (wsClient) {
    wsClient.dispose();
  }

  // this needs to be done first so resetStore re-queries
  // without authorization headers
  updateSignInStateInCache({
    isSignedIn: false,
    isAlreadySignedInOnce: true,
    isStudentSignedIn: false,
  });

  clearSessionData();
  clearStudentUserData();
  clearStudentSession();

  if (signOutServerSide && token) {
    apolloClient
      ?.getClient()
      ?.clearStore()
      .then(callback)
      .finally(() => {
        signOutAtServer(token);
      });
  } else {
    callback?.();
  }
}

type ClientSignIn = {
  isSignedIn: boolean;
  isAlreadySignedInOnce: boolean;
  isStudentSignedIn: boolean;
};

export function updateSignInStateInCache({
  isSignedIn,
  isAlreadySignedInOnce,
  isStudentSignedIn = false,
}: ClientSignIn) {
  signInStateVar({
    isSignedIn,
    isAlreadySignedInOnce,
    isStudentSignedIn,
  });
}

export function setSkipChooseClassPath(redirectPath: string = '') {
  apolloClient.getClient().writeQuery({
    query: SKIP_CHOOSE_CLASS_REDIRECTION_PATH,
    data: {
      skipChooseClassRedirectionPath: redirectPath
        ? redirectPath
        : window.location.pathname,
    },
  });
}

export function pushSignInToGtm(eventData: DataTrackerEventData) {
  pushDataTrackerEvent(
    EVENT_CATEGORY_AUTHENTICATION,
    EVENT_ACTION_SIGN_IN,
    eventData
  );
}

export function pushSignInEClicks(userId: number, position: string) {
  pushEClicksDataEvent({
    event: 'login',
    status: E_CLICKS_LOGGED_IN,
    userId,
    position,
  });
}

interface ParseRouteForClassSelectionResult {
  hasClassSelectionParam: boolean;
  routeWithoutParam: string;
}

export function parseRouteForClassSelection(
  route: string
): ParseRouteForClassSelectionResult {
  const routeParts = route.split('?');
  const routePath = routeParts[0];
  const routeSearchParams = routeParts[1]
    ? new URLSearchParams(routeParts[1])
    : null;
  const hasClassSelectionParam =
    routeSearchParams?.get(CLASS_SELECTION_PARAM) === 'true';

  if (hasClassSelectionParam && routeSearchParams) {
    routeSearchParams.delete(CLASS_SELECTION_PARAM);
    const remainingParams = routeSearchParams.toString();
    const routeWithoutParam = remainingParams
      ? `${routePath}?${remainingParams}`
      : routePath;
    return { hasClassSelectionParam, routeWithoutParam };
  }

  return { hasClassSelectionParam, routeWithoutParam: route };
}
